import { splitHdnValue } from "@/helpers/hdnHelpers";
import type {
	ExistingMortgage,
	InventarisationCustomer,
} from "@loanlink-nl/model-app";
import type {
	HdnDossierTable,
	HdnInformatieAanvraagTable,
} from "@loanlink-nl/zero";

const editableMortgageFieldStatuses = new Set([
	"04 Informatieverzoek niet te verwerken, zie toelichting",
	"05 Informatieverzoek afgewezen",
	"06 Partij niet gerechtigd om data op te halen",
	"07 Lening is afgelost",
	"08 Informatieverzoek technisch niet te verwerken, zie toelichting",
	"80 Partij niet geautoriseerd (zie toelichting)",
	"81 Autorisatie niet verleend door HDN Poortwachter (zie toelichting)",
]);

const statusMeta: Record<
	string,
	{
		name?: string;
		description: string;
		icon: string;
		colorClass: string;
	}
> = {
	draft: {
		name: "Concept",
		description: "Verzoek is nog niet verstuurd.",
		icon: "trip_origin",
		colorClass: "text-gray-500",
	},
	sent: {
		name: "Verzonden",
		description: "Verzoek is verstuurd via HDN.",
		icon: "send",
		colorClass: "text-gray-500",
	},
	"01 Informatieverzoek ontvangen": {
		description: "Het verzoek is ontvangen door de geldverstrekker.",
		icon: "pending",
		colorClass: "text-yellow-500",
	},
	"02 Informatieverzoek verwerkt": {
		description: "Het verzoek is verwerkt.",
		icon: "check",
		colorClass: "text-green-500",
	},
	"03 Informatieverzoek in behandeling genomen": {
		description: "Het verzoek is in behandeling genomen.",
		icon: "visibility",
		colorClass: "text-blue-500",
	},
	"04 Informatieverzoek niet te verwerken, zie toelichting": {
		description: "Het verzoek kan niet worden verwerkt, zie de toelichting.",
		icon: "cancel",
		colorClass: "text-red-500",
	},
	"05 Informatieverzoek afgewezen": {
		description: "Het verzoek is afgewezen.",
		icon: "cancel",
		colorClass: "text-red-500",
	},
	"06 Partij niet gerechtigd om data op te halen": {
		description: "De partij is niet gerechtigd om deze gegevens op te halen.",
		icon: "cancel",
		colorClass: "text-red-500",
	},
	"07 Lening is afgelost": {
		description: "De lening is reeds afgelost.",
		icon: "warning",
		colorClass: "text-gray-500",
	},
	"08 Informatieverzoek technisch niet te verwerken, zie toelichting": {
		description:
			"Het verzoek is technisch niet te verwerken, zie de toelichting.",
		icon: "cancel",
		colorClass: "text-red-500",
	},
	"80 Partij niet geautoriseerd (zie toelichting)": {
		description: "De partij is niet geautoriseerd, zie de toelichting.",
		icon: "cancel",
		colorClass: "text-red-500",
	},
	"81 Autorisatie niet verleend door HDN Poortwachter (zie toelichting)": {
		description: "Autorisatie is niet verleend door HDN Poortwachter.",
		icon: "cancel",
		colorClass: "text-red-500",
	},
	"82 Aanroep HDN Poortwachter mislukt. Bericht wel verzonden.": {
		description:
			"De aanroep van HDN Poortwachter is mislukt, maar het bericht is wel verzonden.",
		icon: "cancel",
		colorClass: "text-red-500",
	},
} as const;

export type InformatieAanvraagWithDossier = HdnInformatieAanvraagTable & {
	hdnDossier?: HdnDossierTable | null;
};

export function informatieAanvraagMatchesMortgage(
	informatieAanvraag: InformatieAanvraagWithDossier,
	existingMortgage?: ExistingMortgage,
) {
	if (!existingMortgage) {
		return false;
	}

	return informatieAanvraag.existingMortgageId === existingMortgage.id;
}

function normalizeName(value?: string) {
	return value?.trim().toLocaleLowerCase();
}

export function getMortgageOwnerFromInformatieAanvraag(
	informatieAanvraag: Pick<InformatieAanvraagWithDossier, "content">,
	customers: Pick<InventarisationCustomer, "id" | "personDetails">[],
): ExistingMortgage["owner"] | undefined {
	const requestedCustomers = Array.isArray(informatieAanvraag.content.customers)
		? informatieAanvraag.content.customers
		: [];

	const matchedCustomerIds = new Set<string>();

	for (const requestedCustomer of requestedCustomers) {
		const matchedCustomer = customers.find(
			(customer) =>
				normalizeName(customer.personDetails.lastName) ===
					normalizeName(requestedCustomer.lastname) &&
				customer.personDetails.birthDate === requestedCustomer.birthDate,
		);

		if (matchedCustomer) {
			matchedCustomerIds.add(matchedCustomer.id);
		}
	}

	if (matchedCustomerIds.size === 0) {
		return undefined;
	}

	if (matchedCustomerIds.size > 1) {
		return "both";
	}

	return [...matchedCustomerIds][0];
}

export function getInformatieAanvraagStatus(
	status?: string,
	statusMessage?: string,
) {
	const meta = status ? statusMeta[status] : undefined;
	const [code, label] = splitHdnValue(status ?? "");

	return {
		icon: meta?.icon ?? "help",
		colorClass: meta?.colorClass ?? "text-gray-500",
		displayName: meta?.name ?? label ?? "Onbekend",
		description: statusMessage || meta?.description,
		statusCodeSuffix: !meta?.name && label && code ? ` (SX${code})` : undefined,
	};
}

export function canEditMortgageReferenceFields(
	hasInformatieAanvraag: boolean,
	status?: string,
) {
	if (!hasInformatieAanvraag) {
		return true;
	}

	return status !== undefined && editableMortgageFieldStatuses.has(status);
}
